class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.focusRecoveryFrame = null;
    this.scheduleFocusRecovery = () => {
      if (!this.classList.contains('active') || this.rechargeDialog) return;
      if (this.focusRecoveryFrame !== null) return;

      this.focusRecoveryFrame = requestAnimationFrame(() => {
        this.focusRecoveryFrame = null;
        if (!this.classList.contains('active') || this.rechargeDialog) return;

        const dialog = this.querySelector('.drawer__inner');
        if (!dialog || dialog.contains(document.activeElement)) return;
        dialog.querySelector('.drawer__close')?.focus();
      });
    };
    this.handleFocusEscape = (event) => {
      if (!this.classList.contains('active') || this.rechargeDialog) return;
      const dialog = this.querySelector('.drawer__inner');
      const destination = event.type === 'focusout' ? event.relatedTarget : event.target;
      if (!dialog || !destination || !dialog.contains(destination)) this.scheduleFocusRecovery();
    };
    this.handleRechargeDismiss = (event) => {
      if (event.key === 'Escape' || (event.type === 'click' && event.target.closest?.('#cart-overlay-drawer > button'))) {
        this.closeAfterRecharge = true;
      }
    };
    this.rechargeFocusGuards = new WeakMap();
    this.handleRechargeFocusIntent = (event) => {
      const guard = this.rechargeFocusGuard;
      if (event.isTrusted && guard?.dialog?.contains(event.target)) guard.intent = event;
    };
    this.focusObserver = new MutationObserver(() => this.syncFocusOwner());
    this.focusObserver.observe(this, { childList: true, subtree: true });
  }

  syncFocusOwner() {
    if (!this.classList.contains('active')) return;
    const host = document.getElementById('recharge-storefront-experiences__cart-overlay');
    const root = host?.shadowRoot;
    if (root && root !== this.rechargeShadowRoot) {
      this.rechargeShadowRoot = root;
      this.focusObserver.observe(root, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'],
      });
      root.addEventListener('keydown', this.handleRechargeDismiss, true);
      root.addEventListener('click', this.handleRechargeDismiss, true);
      ['keydown', 'keyup', 'pointerdown', 'pointerup', 'click'].forEach((type) =>
        root.addEventListener(type, this.handleRechargeFocusIntent, true));
    }

    // shortcut: Recharge's open shadow-root dialog signals ownership; update if its mount contract changes.
    const dialog = root?.querySelector('[role="dialog"][data-state="open"]');
    if (dialog && dialog !== this.rechargeDialog) {
      this.rechargeDialog = dialog;
      this.closeAfterRecharge = false;
      removeTrapFocus();
      setBodySiblingsInert(this, false);
      this.rechargeHost = host;
      setBodySiblingsInert(host, true);
      this.guardRechargeInitialFocus(dialog);
      if (!dialog.contains(root.activeElement) || root.activeElement === dialog) {
        (dialog.querySelector('button') || dialog).focus();
      }
    } else if (!dialog && this.rechargeDialog) {
      this.releaseRechargeFocusGuard();
      this.rechargeDialog = null;
      setBodySiblingsInert(this.rechargeHost, false);
      this.rechargeHost = null;
      if (this.closeAfterRecharge) {
        this.close();
        return;
      }
      setBodySiblingsInert(this, true);
      this.focusDrawer();
      this.refreshDrawerContent();
    }
    if (dialog) this.guardRechargeInitialFocus(dialog);
    this.scheduleFocusRecovery();
  }

  guardRechargeInitialFocus(dialog) {
    // shortcut: Recharge initializes through this Close button; revisit if its focus contract changes.
    const button = dialog.querySelector('#cart-overlay-drawer > button');
    if (!button || (this.rechargeFocusGuard?.button === button && this.rechargeFocusGuard.dialog === dialog)) return;
    this.releaseRechargeFocusGuard();
    let guard = this.rechargeFocusGuards.get(button);
    if (!guard) {
      guard = { button, dialog: null, intent: null };
      const focus = button.focus;
      button.focus = (...args) => {
        const owner = guard.dialog;
        if (!owner?.isConnected || owner.dataset.state !== 'open' || this.rechargeDialog !== owner) return;
        const active = owner.getRootNode().activeElement;
        const userInput = guard.intent?.eventPhase > Event.NONE;
        if (owner.contains(active) && active !== owner && active !== button && !userInput) return;
        return focus.apply(button, args);
      };
      this.rechargeFocusGuards.set(button, guard);
    }
    guard.dialog = dialog;
    this.rechargeFocusGuard = guard;
  }

  releaseRechargeFocusGuard() {
    if (!this.rechargeFocusGuard) return;
    this.rechargeFocusGuard.dialog = null;
    this.rechargeFocusGuard.intent = null;
    this.rechargeFocusGuard = null;
  }

  focusDrawer(elementToFocus = null) {
    if (!this.classList.contains('active') || this.rechargeDialog) return;
    const dialog = this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner');
    const closeButton = dialog?.querySelector('.drawer__close');
    if (!dialog || !closeButton) return;
    trapFocus(dialog, elementToFocus || closeButton);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.openTimeout) return;

    if (this.classList.contains('active')) {
      this.focusDrawer();
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimeout = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.openTimeout = null;
      document.addEventListener('focusin', this.handleFocusEscape, true);
      document.addEventListener('focusout', this.handleFocusEscape, true);
      this.syncFocusOwner();
      if (!this.rechargeDialog) {
        this.focusDrawer();
        setBodySiblingsInert(this, true);
      }
      this.scheduleFocusRecovery();
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimeout);
    this.openTimeout = null;
    document.removeEventListener('focusin', this.handleFocusEscape, true);
    document.removeEventListener('focusout', this.handleFocusEscape, true);
    if (this.focusRecoveryFrame !== null) {
      cancelAnimationFrame(this.focusRecoveryFrame);
      this.focusRecoveryFrame = null;
    }
    this.classList.remove('active');
    this.releaseRechargeFocusGuard();
    document.body.classList.remove('overflow-hidden');
    if (this.rechargeHost) {
      setBodySiblingsInert(this.rechargeHost, false);
      this.rechargeHost = null;
      this.rechargeDialog = null;
    }
    setBodySiblingsInert(this, false);
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const parsedSections =
      parsedState.sections && typeof parsedState.sections === 'object' ? parsedState.sections : null;
    let drawerSectionUpdated = false;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedSections ? parsedSections[section.id] : null;
      if (!sectionHtml) return;
      const updatedHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (updatedHtml === null) return;
      sectionElement.innerHTML = updatedHtml;
      if (section.id === 'cart-drawer') {
        drawerSectionUpdated = true;
      }
    });

    const openDrawer = () => {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    };

    if (!drawerSectionUpdated) {
      this.refreshDrawerContent().finally(openDrawer);
      return;
    }

    openDrawer();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshDrawerContent() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');
        if (sourceDrawer) {
          this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        }

        const sourceCartDrawer = html.querySelector('#CartDrawer');
        const targetCartDrawer = this.querySelector('#CartDrawer');
        if (sourceCartDrawer && targetCartDrawer) {
          targetCartDrawer.innerHTML = sourceCartDrawer.innerHTML;
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.addEventListener('change', this.onVariantSelectChange.bind(this));
    this.addEventListener('change', this.onSellingPlanSelectChange.bind(this));
    this.addEventListener('click', this.onSubscribeButtonClick.bind(this));
  }

  onVariantSelectChange(event) {
    const variantSelect = event.target.closest('.cart-drawer__variant-select');
    if (!variantSelect) return;

    const line = Number.parseInt(variantSelect.dataset.line, 10);
    const quantity = Number.parseInt(variantSelect.dataset.quantity, 10);
    const selectedVariantId = (variantSelect.value || '').trim();
    const currentVariantId = (variantSelect.dataset.currentVariantId || '').trim();
    const currentSellingPlanId = (variantSelect.dataset.currentSellingPlanId || '').trim();
    const hasValidVariantId = /^\d+$/.test(selectedVariantId);

    if (
      Number.isNaN(line) ||
      Number.isNaN(quantity) ||
      !hasValidVariantId ||
      currentVariantId === '' ||
      selectedVariantId === currentVariantId
    ) {
      return;
    }

    this.updateLineItemVariant(line, quantity, selectedVariantId, variantSelect, currentVariantId, currentSellingPlanId);
  }

  onSellingPlanSelectChange(event) {
    const sellingPlanSelect = event.target.closest('.cart-drawer__selling-plan-select');
    if (!sellingPlanSelect) return;

    const line = Number.parseInt(sellingPlanSelect.dataset.line, 10);
    const planQuantity = Number(sellingPlanSelect.selectedOptions[0]?.dataset.productQuantity || 0);
    const quantity = planQuantity > 0 ? planQuantity : Number.parseInt(sellingPlanSelect.dataset.quantity, 10);
    const selectedSellingPlanId = (sellingPlanSelect.value || '').trim();
    const currentSellingPlanId = (sellingPlanSelect.dataset.currentSellingPlanId || '').trim();
    const isValidSellingPlanChange = selectedSellingPlanId === '' || /^\d+$/.test(selectedSellingPlanId);

    if (
      Number.isNaN(line) ||
      Number.isNaN(quantity) ||
      !Number.isSafeInteger(planQuantity) ||
      planQuantity < 0 ||
      !isValidSellingPlanChange ||
      selectedSellingPlanId === currentSellingPlanId
    ) {
      return;
    }

    if (selectedSellingPlanId === '') {
      this.setSellingPlanSelectLoading(sellingPlanSelect, true);
    }
    this.updateLineItemSellingPlan(line, quantity, selectedSellingPlanId, sellingPlanSelect, currentSellingPlanId);
  }

  onSubscribeButtonClick(event) {
    const subscribeButton = event.target.closest('.cart-drawer__subscribe-button');
    if (!subscribeButton) return;

    const targetId = subscribeButton.getAttribute('aria-controls');
    const target = targetId ? document.getElementById(targetId) : null;
    const select = target?.querySelector('.cart-drawer__selling-plan-select');
    if (!target || !select) return;

    this.setSubscribeButtonLoading(subscribeButton, true);
    if (select.value) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.setSubscribeButtonLoading(subscribeButton, false);
    }
  }

  setSubscribeButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.defaultText = button.dataset.defaultText || button.textContent.trim();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      button.innerHTML = `
        <span class="cart-drawer__subscribe-loading-spinner" aria-hidden="true"></span>
        <span class="cart-drawer__subscribe-loading-text">Loading</span>
        <span class="cart-drawer__subscribe-loading-dots" aria-hidden="true">
          <span></span><span></span><span></span>
        </span>
      `;
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
    if (button.dataset.defaultText) button.textContent = button.dataset.defaultText;
  }

  setSellingPlanSelectLoading(select, isLoading) {
    const wrapper = select?.closest('.cart-item__selling-plan-picker');
    if (!select || !wrapper) return;

    let loading = wrapper.querySelector('.cart-drawer__selling-plan-loading');
    if (isLoading) {
      select.disabled = true;
      select.setAttribute('aria-busy', 'true');
      wrapper.classList.add('is-loading');

      if (!loading) {
        loading = document.createElement('span');
        loading.className = 'cart-drawer__selling-plan-loading';
        loading.setAttribute('aria-hidden', 'true');
        loading.innerHTML = `
          <span class="cart-drawer__subscribe-loading-spinner"></span>
          <span class="cart-drawer__subscribe-loading-text">Loading</span>
          <span class="cart-drawer__subscribe-loading-dots">
            <span></span><span></span><span></span>
          </span>
        `;
        wrapper.querySelector('.select')?.append(loading);
      }
      return;
    }

    select.disabled = false;
    select.removeAttribute('aria-busy');
    wrapper.classList.remove('is-loading');
    loading?.remove();
  }

  getLinePositionContext(line) {
    const row = document.getElementById(`CartDrawer-Item-${line}`);
    const tbody = row?.parentElement;
    if (!row || !tbody) return null;

    const rows = Array.from(tbody.children).filter((child) => child.classList.contains('cart-item'));
    const variantSelect = row.querySelector('.cart-drawer__variant-select');
    const sellingPlanSelect = row.querySelector('.cart-drawer__selling-plan-select');

    return {
      index: rows.indexOf(row),
      productId: variantSelect?.dataset.productId || sellingPlanSelect?.dataset.productId || '',
      variantId: variantSelect?.dataset.currentVariantId || sellingPlanSelect?.dataset.currentVariantId || '',
    };
  }

  restoreLinePosition(context, matchesRow) {
    if (!context || context.index < 0 || typeof matchesRow !== 'function') return null;

    const tbody = document.querySelector('#CartDrawer-CartItems tbody');
    if (!tbody) return null;

    const rows = Array.from(tbody.children).filter((child) => child.classList.contains('cart-item'));
    const row = rows.find(matchesRow);
    if (!row) return null;

    const rowsWithoutTarget = rows.filter((item) => item !== row);
    tbody.insertBefore(row, rowsWithoutTarget[context.index] || null);
    return row;
  }

  restoreDrawerScroll(scrollTop) {
    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.scrollTop = scrollTop;
  }

  updateLineItemVariant(line, quantity, variantId, variantSelect, previousVariantId, sellingPlanId = '') {
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker('variant-change:user-action');
    this.enableLoading(line);
    const sectionIds = this.getSectionsToRender().map((section) => section.section);
    const linePosition = this.getLinePositionContext(line);
    const drawerScrollTop = this.scrollTop;
    const previousItemKey = (variantSelect.dataset.itemKey || '').trim();
    const productId = (variantSelect.dataset.productId || '').trim();
    if (previousItemKey === '') {
      variantSelect.value = previousVariantId;
      const errorMessage = window.cartStrings.error;
      this.updateLiveRegions(line, errorMessage);
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      errors.textContent = errorMessage;
      this.disableLoading(line);
      CartPerformance.measureFromMarker('variant-change:user-action', cartPerformanceUpdateMarker);
      return;
    }

    if (/^\d+$/.test(sellingPlanId)) {
      this.replaceLineItemVariantWithSellingPlan({
        line,
        quantity,
        variantId,
        sellingPlanId,
        previousItemKey,
        previousVariantId,
        variantSelect,
        productId,
        sectionIds,
        linePosition,
        drawerScrollTop,
        cartPerformanceUpdateMarker,
      });
      return;
    }

    const updates = {};
    updates[previousItemKey] = 0;
    updates[variantId] = quantity;

    const updatePayload = {
      updates,
      sections: sectionIds,
      sections_url: window.location.pathname,
    };

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(updatePayload) },
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const parsedSections =
          parsedState.sections && typeof parsedState.sections === 'object' ? parsedState.sections : null;
        if (parsedState.errors) {
          throw new Error(
            typeof parsedState.errors === 'string' ? parsedState.errors : parsedState.message || window.cartStrings.error
          );
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        let drawerSectionUpdated = false;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          const sectionHtml = parsedSections ? parsedSections[section.section] : null;
          if (!sectionHtml) return;

          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
          const updatedHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (updatedHtml === null) return;
          elementToReplace.innerHTML = updatedHtml;
          if (section.section === 'cart-drawer') {
            drawerSectionUpdated = true;
          }
        });

        if (!drawerSectionUpdated) {
          this.onCartUpdate();
        }

        this.updateLiveRegions(line, '');
        this.restoreLinePosition(linePosition, (row) => {
          const select = row.querySelector('.cart-drawer__variant-select');
          if (!select || select.dataset.currentVariantId !== variantId) return false;
          if (productId !== '' && select.dataset.productId !== productId) return false;
          return true;
        });
        this.restoreDrawerScroll(drawerScrollTop);

        const updatedVariantSelect = Array.from(document.querySelectorAll('.cart-drawer__variant-select')).find((select) => {
          if (select.dataset.currentVariantId !== variantId) return false;
          if (productId !== '' && select.dataset.productId !== productId) return false;
          return true;
        });
        if (updatedVariantSelect) {
          cartDrawerWrapper ? cartDrawerWrapper.focusDrawer(updatedVariantSelect) : updatedVariantSelect.focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch((error) => {
        variantSelect.value = previousVariantId;
        const errorMessage =
          typeof error?.message === 'string' && error.message.length > 0 ? error.message : window.cartStrings.error;
        this.updateLiveRegions(line, errorMessage);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = errorMessage;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker('variant-change:user-action', cartPerformanceUpdateMarker);
      });
  }

  replaceLineItemVariantWithSellingPlan({
    line,
    quantity,
    variantId,
    sellingPlanId,
    previousItemKey,
    previousVariantId,
    variantSelect,
    productId,
    sectionIds,
    linePosition,
    drawerScrollTop,
    cartPerformanceUpdateMarker,
  }) {
    const addPayload = {
      items: [
        {
          id: Number.parseInt(variantId, 10),
          quantity,
          selling_plan: Number.parseInt(sellingPlanId, 10),
        },
      ],
    };

    fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(addPayload) },
    })
      .then((response) => response.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || window.cartStrings.error);
        }

        const updatePayload = {
          updates: {
            [previousItemKey]: 0,
          },
          sections: sectionIds,
          sections_url: window.location.pathname,
        };

        return fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body: JSON.stringify(updatePayload) },
        });
      })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const parsedSections =
          parsedState.sections && typeof parsedState.sections === 'object' ? parsedState.sections : null;
        if (parsedState.errors) {
          throw new Error(
            typeof parsedState.errors === 'string' ? parsedState.errors : parsedState.message || window.cartStrings.error
          );
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        let drawerSectionUpdated = false;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          const sectionHtml = parsedSections ? parsedSections[section.section] : null;
          if (!sectionHtml) return;

          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
          const updatedHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (updatedHtml === null) return;
          elementToReplace.innerHTML = updatedHtml;
          if (section.section === 'cart-drawer') {
            drawerSectionUpdated = true;
          }
        });

        if (!drawerSectionUpdated) {
          this.onCartUpdate();
        }

        this.updateLiveRegions(line, '');
        this.restoreLinePosition(linePosition, (row) => {
          const select = row.querySelector('.cart-drawer__variant-select');
          if (!select || select.dataset.currentVariantId !== variantId) return false;
          if (select.dataset.currentSellingPlanId !== sellingPlanId) return false;
          if (productId !== '' && select.dataset.productId !== productId) return false;
          return true;
        });
        this.restoreDrawerScroll(drawerScrollTop);

        const updatedVariantSelect = Array.from(document.querySelectorAll('.cart-drawer__variant-select')).find((select) => {
          if (select.dataset.currentVariantId !== variantId) return false;
          if (select.dataset.currentSellingPlanId !== sellingPlanId) return false;
          if (productId !== '' && select.dataset.productId !== productId) return false;
          return true;
        });
        if (updatedVariantSelect) {
          cartDrawerWrapper ? cartDrawerWrapper.focusDrawer(updatedVariantSelect) : updatedVariantSelect.focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch((error) => {
        variantSelect.value = previousVariantId;
        const errorMessage =
          typeof error?.message === 'string' && error.message.length > 0 ? error.message : window.cartStrings.error;
        this.updateLiveRegions(line, errorMessage);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = errorMessage;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker('variant-change:user-action', cartPerformanceUpdateMarker);
      });
  }

  updateLineItemSellingPlan(line, quantity, sellingPlanId, sellingPlanSelect, previousSellingPlanId) {
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker('selling-plan-change:user-action');
    this.enableLoading(line);
    const sectionIds = this.getSectionsToRender().map((section) => section.section);
    const linePosition = this.getLinePositionContext(line);
    const drawerScrollTop = this.scrollTop;
    const productId = (sellingPlanSelect.dataset.productId || '').trim();
    const variantId = (sellingPlanSelect.dataset.currentVariantId || '').trim();

    const changePayload = {
      line,
      quantity,
      selling_plan: sellingPlanId === '' ? null : sellingPlanId,
      sections: sectionIds,
      sections_url: window.location.pathname,
    };

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(changePayload) },
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const parsedSections =
          parsedState.sections && typeof parsedState.sections === 'object' ? parsedState.sections : null;
        if (parsedState.errors) {
          throw new Error(
            typeof parsedState.errors === 'string' ? parsedState.errors : parsedState.message || window.cartStrings.error
          );
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        let drawerSectionUpdated = false;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          const sectionHtml = parsedSections ? parsedSections[section.section] : null;
          if (!sectionHtml) return;

          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
          const updatedHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (updatedHtml === null) return;
          elementToReplace.innerHTML = updatedHtml;
          if (section.section === 'cart-drawer') {
            drawerSectionUpdated = true;
          }
        });

        if (!drawerSectionUpdated) {
          this.onCartUpdate();
        }

        this.updateLiveRegions(line, '');
        this.restoreLinePosition(linePosition, (row) => {
          const select = row.querySelector('.cart-drawer__selling-plan-select');
          if (!select || select.dataset.currentSellingPlanId !== sellingPlanId) return false;
          if (productId !== '' && select.dataset.productId !== productId) return false;
          if (variantId !== '' && select.dataset.currentVariantId !== variantId) return false;
          return true;
        });
        this.restoreDrawerScroll(drawerScrollTop);

        const updatedSellingPlanSelect = Array.from(document.querySelectorAll('.cart-drawer__selling-plan-select')).find(
          (select) => {
            if (select.dataset.currentSellingPlanId !== sellingPlanId) return false;
            if (productId !== '' && select.dataset.productId !== productId) return false;
            if (variantId !== '' && select.dataset.currentVariantId !== variantId) return false;
            return true;
          }
        );
        if (updatedSellingPlanSelect) {
          cartDrawerWrapper ? cartDrawerWrapper.focusDrawer(updatedSellingPlanSelect) : updatedSellingPlanSelect.focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch((error) => {
        sellingPlanSelect.value = previousSellingPlanId;
        this.setSellingPlanSelectLoading(sellingPlanSelect, false);
        const subscribeButton = document.querySelector(
          `.cart-drawer__subscribe-button[aria-controls="${sellingPlanSelect.closest('.cart-item__selling-plan-picker')?.id}"]`
        );
        this.setSubscribeButtonLoading(subscribeButton, false);
        const errorMessage =
          typeof error?.message === 'string' && error.message.length > 0 ? error.message : window.cartStrings.error;
        this.updateLiveRegions(line, errorMessage);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = errorMessage;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker('selling-plan-change:user-action', cartPerformanceUpdateMarker);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
